import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi, type CertificateTemplateDto, type CertificateDesignDto, type CertificateImageDto } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { Button } from '@/components/ui/button'
import { Plus, Pencil, Trash2, ImagePlus, Check, X, Award, Images, Loader2, Lock, FileText } from 'lucide-react'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import CertificateFieldDesigner from './CertificateFieldDesigner'
import ThirdPartyCertificateUploader from './ThirdPartyCertificateUploader'

// ─── Tab type ────────────────────────────────────────────────────────────────
type Tab = 'designs' | 'backgrounds' | 'images' | 'thirdParty'

const TAB_LABELS: Record<Tab, string> = {
  designs: 'Designs',
  backgrounds: 'Backgrounds',
  images: 'Images',
  thirdParty: 'Third-party',
}

// ─── Backgrounds tab ─────────────────────────────────────────────────────────
function BackgroundsTab() {
  const queryClient = useQueryClient()
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['certificate-templates'],
    queryFn: () => adminApi.getCertificateTemplates().then(r => r.data),
  })

  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState({ name: '', description: '', file: null as File | null })
  const createFileInputRef = useRef<HTMLInputElement>(null)

  const resetCreateForm = () => { setCreating(false); setForm({ name: '', description: '', file: null }) }

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await adminApi.createCertificateTemplate({ name: form.name.trim(), description: form.description.trim() || null })
      if (form.file) await adminApi.uploadCertificateTemplateBackground(res.data.id, form.file)
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['certificate-templates'] }); resetCreateForm() },
  })
  const updateMutation = useMutation({
    mutationFn: (id: string) => adminApi.updateCertificateTemplate(id, { name: form.name.trim(), description: form.description.trim() || null }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['certificate-templates'] }); setEditingId(null) },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCertificateTemplate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certificate-templates'] }),
    onError: () => alert('This background is used by one or more certificate designs and cannot be deleted.'),
  })
  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => adminApi.uploadCertificateTemplateBackground(id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certificate-templates'] }),
  })

  if (isLoading) return <p className="text-gray-400 text-sm">Loading…</p>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">Upload certificate background images. A4 proportions work best — landscape (297×210 mm) or portrait (210×297 mm).</p>
        <Button size="sm" className="gap-1.5" onClick={() => { setCreating(true); setEditingId(null); setForm({ name: '', description: '', file: null }) }}>
          <Plus size={15} /> New background
        </Button>
      </div>

      {creating && (
        <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <p className="font-semibold text-gray-800 mb-3">New background</p>
          <div className="flex gap-4">
            {/* File picker */}
            <div
              className="w-48 h-32 shrink-0 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center gap-1.5 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
              onClick={() => createFileInputRef.current?.click()}
            >
              {form.file
                ? (form.file.type === 'application/pdf' || form.file.name.endsWith('.pdf'))
                    ? <div className="flex flex-col items-center justify-center gap-1.5 w-full h-full px-2">
                        <FileText size={28} className="text-indigo-500 shrink-0" />
                        <span className="text-xs text-gray-600 text-center leading-tight line-clamp-2 break-all">{form.file.name}</span>
                      </div>
                    : <img src={URL.createObjectURL(form.file)} alt="" className="w-full h-full object-cover rounded-lg" />
                : <>
                    <ImagePlus size={22} className="text-gray-400" />
                    <span className="text-xs text-gray-400 text-center px-2">Click to upload image or PDF</span>
                  </>
              }
            </div>
            <input ref={createFileInputRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) setForm(v => ({ ...v, file: f, name: v.name || f.name.replace(/\.[^.]+$/, '') })); e.target.value = '' }} />

            {/* Name / description */}
            <div className="flex-1 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                <input className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Gold Border" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional note" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !form.name.trim()} className="gap-1">
                  <Check size={14} /> {createMutation.isPending ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" onClick={resetCreateForm}><X size={14} /> Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map(t => (
          <TemplateCard key={t.id} template={t}
            editing={editingId === t.id} form={form} onChange={f => setForm(prev => ({ ...prev, ...f }))}
            onEdit={() => { setEditingId(t.id); setForm({ name: t.name, description: t.description ?? '', file: null }); setCreating(false) }}
            onSave={() => updateMutation.mutate(t.id)}
            onCancel={() => setEditingId(null)}
            onDelete={() => { if (confirm(`Delete "${t.name}"?`)) deleteMutation.mutate(t.id) }}
            onUpload={file => uploadMutation.mutate({ id: t.id, file })}
            saving={updateMutation.isPending}
            uploading={uploadMutation.isPending && uploadMutation.variables?.id === t.id}
          />
        ))}
        {templates.length === 0 && !creating && (
          <div className="col-span-3 text-center py-16 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
            <p className="font-medium">No backgrounds yet</p>
            <p className="text-sm mt-1">Create one to get started</p>
          </div>
        )}
      </div>
    </div>
  )
}

function TemplateCard({ template, editing, form, onChange, onEdit, onSave, onCancel, onDelete, onUpload, saving, uploading }: {
  template: CertificateTemplateDto; editing: boolean
  form: { name: string; description: string }
  onChange: (f: { name: string; description: string }) => void
  onEdit: () => void; onSave: () => void; onCancel: () => void; onDelete: () => void
  onUpload: (file: File) => void; saving: boolean; uploading: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <div className="relative h-40 bg-gray-100 flex items-center justify-center cursor-pointer group"
        onClick={() => fileInputRef.current?.click()} title="Click to upload background image">
        {template.backgroundImageUrl
          ? <img src={template.backgroundImageUrl} alt={template.name} className="h-full w-full object-cover" />
          : <div className="text-center text-gray-400"><ImagePlus size={28} className="mx-auto mb-1" /><p className="text-xs">Upload background</p></div>}
        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <p className="text-white text-xs font-medium">{uploading ? 'Uploading…' : 'Change background'}</p>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = '' }} />
      </div>
      <div className="p-4">
        {editing ? (
          <>
            <input className="w-full text-sm font-semibold border border-gray-300 rounded-md px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.name} onChange={e => onChange({ ...form, name: e.target.value })} autoFocus />
            <input className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.description} onChange={e => onChange({ ...form, description: e.target.value })} placeholder="Description" />
            <div className="flex gap-2">
              <Button size="sm" onClick={onSave} disabled={saving || !form.name.trim()} className="gap-1 h-7 text-xs"><Check size={13} /> {saving ? 'Saving…' : 'Save'}</Button>
              <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs">Cancel</Button>
            </div>
          </>
        ) : (
          <>
            <p className="font-semibold text-gray-900 text-sm">{template.name}</p>
            {template.description && <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>}
            <div className="flex gap-1.5 mt-3">
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={onEdit}><Pencil size={12} /> Edit</Button>
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={onDelete}><Trash2 size={12} /> Delete</Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Images tab ──────────────────────────────────────────────────────────────
function ImagesTab() {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [pendingName, setPendingName] = useState('')

  const { data: images = [], isLoading } = useQuery({
    queryKey: ['certificate-images'],
    queryFn: () => adminApi.getCertificateImages().then(r => r.data),
  })

  const uploadMutation = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) =>
      adminApi.uploadCertificateImage(name, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate-images'] })
      setPendingFile(null)
      setPendingName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCertificateImage(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certificate-images'] }),
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setPendingFile(f)
    setPendingName(f.name.replace(/\.[^.]+$/, ''))
    e.target.value = ''
  }

  if (isLoading) return <p className="text-gray-400 text-sm">Loading…</p>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          Upload images (logos, stamps, signatures) to use across any certificate design.
        </p>
        <Button size="sm" className="gap-1.5" onClick={() => fileInputRef.current?.click()}>
          <ImagePlus size={15} /> Upload image
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>

      {/* Pending upload — name entry */}
      {pendingFile && (
        <div className="mb-4 bg-white border border-indigo-200 rounded-lg p-4 shadow-sm flex items-center gap-4">
          <img src={URL.createObjectURL(pendingFile)} alt="" className="h-16 w-24 object-contain rounded border border-gray-200 bg-gray-50" />
          <div className="flex-1 space-y-2">
            <label className="block text-xs font-medium text-gray-600">Image name</label>
            <input
              autoFocus
              className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={pendingName}
              onChange={e => setPendingName(e.target.value)}
              placeholder="e.g. Gold Stamp"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" className="gap-1" disabled={!pendingName.trim() || uploadMutation.isPending}
              onClick={() => uploadMutation.mutate({ name: pendingName, file: pendingFile })}>
              <Check size={14} /> {uploadMutation.isPending ? 'Uploading…' : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setPendingFile(null); setPendingName('') }}>
              <X size={14} />
            </Button>
          </div>
        </div>
      )}

      {images.length === 0 && !pendingFile ? (
        <div className="text-center py-16 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
          <Images size={32} className="mx-auto mb-2 opacity-40" />
          <p className="font-medium">No images yet</p>
          <p className="text-sm mt-1">Upload images to use in your certificate designs</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {images.map((img: CertificateImageDto) => (
            <div key={img.id} className="group bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <div className="h-28 bg-gray-50 flex items-center justify-center p-3">
                <img src={img.url} alt={img.name} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="px-3 py-2 flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-gray-700 truncate">{img.name}</span>
                <button
                  type="button"
                  className="shrink-0 text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
                  title="Delete image"
                  onClick={() => { if (confirm(`Delete "${img.name}"?`)) deleteMutation.mutate(img.id) }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Designs tab ─────────────────────────────────────────────────────────────
type BackgroundSource = 'upload' | 'system'

function DesignsTab() {
  const queryClient = useQueryClient()
  const { data: designs = [], isLoading } = useQuery({
    queryKey: ['certificate-designs'],
    queryFn: () => adminApi.getCertificateDesigns().then(r => r.data),
  })
  const { data: templates = [] } = useQuery({
    queryKey: ['certificate-templates'],
    queryFn: () => adminApi.getCertificateTemplates().then(r => r.data),
  })
  const { data: systemTemplates = [] } = useQuery({
    queryKey: ['system-certificate-templates'],
    queryFn: () => adminApi.getSystemCertificateTemplates().then(r => r.data),
  })
  const { data: companyProfile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const isPro = companyProfile?.planTier === 'Pro'
  const designLimit = isPro ? 10 : 1

  const [creating, setCreating] = useState(false)
  const [showProDialog, setShowProDialog] = useState(false)
  const [newSource, setNewSource] = useState<BackgroundSource>('system')
  const [newForm, setNewForm] = useState({ name: '', templateId: '', landscapeSystemTemplateId: '', portraitSystemTemplateId: '', colour: '#4F46E5', backgroundColour: '#ffffff', isPortrait: false })
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: () => adminApi.createCertificateDesign({
      name: newForm.name.trim(),
      certificateTemplateId: newSource === 'upload' ? newForm.templateId : null,
      systemCertificateTemplateId: newSource === 'system' ? (newForm.isPortrait ? newForm.portraitSystemTemplateId : newForm.landscapeSystemTemplateId) : null,
      primaryColour: newSource === 'system' ? newForm.colour : null,
      backgroundColour: newSource === 'system' ? newForm.backgroundColour : null,
      isPortrait: newForm.isPortrait,
    }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['certificate-designs'] })
      setCreating(false)
      setNewForm({ name: '', templateId: '', landscapeSystemTemplateId: '', portraitSystemTemplateId: '', colour: '#4F46E5', backgroundColour: '#ffffff', isPortrait: false })
      setExpandedId(res.data.id)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteCertificateDesign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certificate-designs'] }),
    onError: () => alert('This design is assigned to one or more courses and cannot be deleted.'),
  })

  const startCreating = () => {
    setCreating(true)
    setNewSource(systemTemplates.length > 0 ? 'system' : 'upload')
    setNewForm({
      name: '',
      templateId: templates[0]?.id ?? '',
      landscapeSystemTemplateId: systemTemplates.find(t => !t.isPortrait)?.id ?? systemTemplates[0]?.id ?? '',
      portraitSystemTemplateId: systemTemplates.find(t => t.isPortrait)?.id ?? '',
      backgroundColour: '#ffffff',
      colour: '#4F46E5',
      isPortrait: false,
    })
  }

  const isCreateValid = newForm.name.trim() &&
    (newSource === 'upload' ? !!newForm.templateId : !!(newForm.isPortrait ? newForm.portraitSystemTemplateId : newForm.landscapeSystemTemplateId))

  if (isLoading) return <p className="text-gray-400 text-sm">Loading…</p>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500">
            A design combines a background with a field layout. Assign designs to courses.
          </p>
          {!isPro && (
            <p className="text-xs text-gray-400 mt-0.5">
              {designs.length} of {designLimit} design used — <button type="button" onClick={() => setShowProDialog(true)} className="text-amber-600 hover:underline cursor-pointer">upgrade for up to 10</button>
            </p>
          )}
        </div>
        {designs.length >= designLimit && !isPro ? (
          <Button size="sm" className="gap-1.5 bg-gray-100 text-gray-500 border border-gray-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300" onClick={() => setShowProDialog(true)}>
            <Lock size={14} /> New design
            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 -mr-1">Pro</span>
          </Button>
        ) : (
          <Button size="sm" className="gap-1.5" onClick={startCreating}>
            <Plus size={15} /> New design
          </Button>
        )}
      </div>

      <ProUpgradeDialog
        open={showProDialog}
        onClose={() => setShowProDialog(false)}
        featureName="Multiple certificate designs"
      />

      {creating && (
        <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <p className="font-semibold text-gray-800 mb-3">New certificate design</p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Design name *</label>
              <input className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Standard Certificate" autoFocus />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Background source</label>
              <div className="flex gap-3 mt-1.5">
                {([['system', 'System templates'], ['upload', 'Your uploads']] as const).map(([src, label]) => (
                  <label key={src} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                    <input type="radio" name="bg-source" value={src} checked={newSource === src}
                      onChange={() => setNewSource(src)} className="accent-indigo-600" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-3">
            {newSource === 'upload' ? (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Background *</label>
                {templates.length === 0 ? (
                  <p className="text-sm text-amber-600">Create a background in the Backgrounds tab first.</p>
                ) : (
                  <select className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={newForm.templateId} onChange={e => setNewForm(f => ({ ...f, templateId: e.target.value }))}>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">System template *</label>
                  {systemTemplates.length === 0 ? (
                    <p className="text-sm text-amber-600">No system templates available yet.</p>
                  ) : (
                    <select className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={newForm.isPortrait ? newForm.portraitSystemTemplateId : newForm.landscapeSystemTemplateId}
                      onChange={e => { const key = newForm.isPortrait ? 'portraitSystemTemplateId' : 'landscapeSystemTemplateId'; setNewForm(f => ({ ...f, [key]: e.target.value })) }}>
                      {systemTemplates.filter(t => t.isPortrait === newForm.isPortrait).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Colour</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={newForm.colour}
                      onChange={e => setNewForm(f => ({ ...f, colour: e.target.value }))}
                      className="h-9 w-9 cursor-pointer rounded border border-gray-300 p-0.5" />
                    <input type="text" value={newForm.colour}
                      onChange={e => setNewForm(f => ({ ...f, colour: e.target.value }))}
                      className="w-28 text-sm border border-gray-300 rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Orientation */}
          <div className="flex items-center gap-3 my-3">
            <span className="text-xs font-medium text-gray-600">Orientation</span>
            {(['Landscape', 'Portrait'] as const).map(o => (
              <label key={o} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                <input
                  type="radio"
                  name="new-orientation"
                  checked={newForm.isPortrait === (o === 'Portrait')}
                  onChange={() => {
                    const portrait = o === 'Portrait'
                    const key = portrait ? 'portraitSystemTemplateId' : 'landscapeSystemTemplateId'
                    const firstMatch = systemTemplates.find(t => t.isPortrait === portrait)
                    setNewForm(f => ({ ...f, isPortrait: portrait, [key]: f[key] || firstMatch?.id || '' }))
                  }}
                  className="cursor-pointer"
                />
                {o}
              </label>
            ))}
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !isCreateValid} className="gap-1">
              <Check size={14} /> {createMutation.isPending ? 'Creating…' : 'Create & design'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreating(false)}><X size={14} /> Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {designs.map(design => (
          <DesignCard key={design.id} design={design}
            expanded={expandedId === design.id}
            onToggle={() => setExpandedId(expandedId === design.id ? null : design.id)}
            onDelete={() => { if (confirm(`Delete "${design.name}"? This cannot be undone.`)) deleteMutation.mutate(design.id) }}
          />
        ))}
        {designs.length === 0 && !creating && (
          <div className="text-center py-16 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
            <Award size={32} className="mx-auto mb-2 opacity-40" />
            <p className="font-medium">No certificate designs yet</p>
            <p className="text-sm mt-1">Create one to get started</p>
          </div>
        )}
      </div>
    </div>
  )
}

function DesignCard({ design, expanded, onToggle, onDelete }: {
  design: CertificateDesignDto
  expanded: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  const queryClient = useQueryClient()
  const isSystem = !!design.systemCertificateTemplateId

  const [editingName, setEditingName] = useState(false)
  const [autoSaved, setAutoSaved] = useState(false)
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }, [])
  const [nameForm, setNameForm] = useState({
    name: design.name,
    templateId: design.certificateTemplateId ?? '',
    landscapeSystemTemplateId: !design.isPortrait ? (design.systemCertificateTemplateId ?? '') : '',
    portraitSystemTemplateId: design.isPortrait ? (design.systemCertificateTemplateId ?? '') : '',
    colour: design.primaryColour ?? '#4F46E5',
    backgroundColour: design.backgroundColour ?? '#ffffff',
    source: (isSystem ? 'system' : 'upload') as BackgroundSource,
    isPortrait: design.isPortrait,
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['certificate-templates'],
    queryFn: () => adminApi.getCertificateTemplates().then(r => r.data),
  })
  const { data: systemTemplates = [] } = useQuery({
    queryKey: ['system-certificate-templates'],
    queryFn: () => adminApi.getSystemCertificateTemplates().then(r => r.data),
  })

  const updateMutation = useMutation({
    mutationFn: (fieldsJson: string | null) => adminApi.updateCertificateDesign(design.id, {
      name: design.name,
      certificateTemplateId: design.certificateTemplateId,
      systemCertificateTemplateId: design.systemCertificateTemplateId,
      primaryColour: design.primaryColour,
      backgroundColour: design.backgroundColour,
      fieldsJson,
      isPortrait: design.isPortrait,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate-designs'] })
      setAutoSaved(true)
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
      autoSaveTimerRef.current = setTimeout(() => setAutoSaved(false), 2000)
    },
  })

  const saveNameMutation = useMutation({
    mutationFn: () => adminApi.updateCertificateDesign(design.id, {
      name: nameForm.name.trim(),
      certificateTemplateId: nameForm.source === 'upload' ? nameForm.templateId : null,
      systemCertificateTemplateId: nameForm.source === 'system' ? (nameForm.isPortrait ? nameForm.portraitSystemTemplateId : nameForm.landscapeSystemTemplateId) : null,
      primaryColour: nameForm.source === 'system' ? nameForm.colour : null,
      backgroundColour: nameForm.source === 'system' ? nameForm.backgroundColour : null,
      fieldsJson: design.fieldsJson,
      isPortrait: nameForm.isPortrait,
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['certificate-designs'] }); setEditingName(false) },
  })

  const saveSettingsMutation = useMutation({
    mutationFn: (form: typeof nameForm) => adminApi.updateCertificateDesign(design.id, {
      name: design.name,
      certificateTemplateId: form.source === 'upload' ? form.templateId : null,
      systemCertificateTemplateId: form.source === 'system' ? (form.isPortrait ? form.portraitSystemTemplateId : form.landscapeSystemTemplateId) : null,
      primaryColour: form.source === 'system' ? form.colour : null,
      backgroundColour: form.source === 'system' ? form.backgroundColour : null,
      fieldsJson: design.fieldsJson,
      isPortrait: form.isPortrait,
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certificate-designs'] }),
  })

  const cancelEdit = () => {
    setEditingName(false)
    setNameForm({
      name: design.name,
      templateId: design.certificateTemplateId ?? '',
      landscapeSystemTemplateId: !design.isPortrait ? (design.systemCertificateTemplateId ?? '') : '',
      portraitSystemTemplateId: design.isPortrait ? (design.systemCertificateTemplateId ?? '') : '',
      colour: design.primaryColour ?? '#4F46E5',
      backgroundColour: design.backgroundColour ?? '#ffffff',
      source: isSystem ? 'system' : 'upload',
      isPortrait: design.isPortrait,
    })
  }

  // Background thumbnail: tenant upload or system template thumbnail
  const thumbnailUrl = design.templateBackgroundImageUrl
    ?? systemTemplates.find(t => t.id === design.systemCertificateTemplateId)?.thumbnailUrl
    ?? null

  const displayName = design.templateName ?? design.systemTemplateName ?? '—'

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Background thumbnail */}
        <div
          className="w-16 h-10 rounded overflow-hidden bg-gray-100 shrink-0 relative"
          style={{
            background: isSystem ? (design.backgroundColour ?? '#ffffff') : undefined,
            isolation: isSystem ? 'isolate' : undefined,
          }}
        >
          {thumbnailUrl
            ? (
              <>
                <img src={thumbnailUrl} alt="" className="w-full h-full object-cover" style={{ mixBlendMode: isSystem ? 'multiply' : undefined }} />
                {isSystem && design.primaryColour && (
                  <div className="absolute inset-0" style={{ backgroundColor: design.primaryColour, mixBlendMode: 'screen' }} />
                )}
              </>
            )
            : <div className="w-full h-full flex items-center justify-center"><ImagePlus size={14} className="text-gray-400" /></div>}
        </div>

        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  className="text-sm font-semibold border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={nameForm.name} onChange={e => setNameForm(f => ({ ...f, name: e.target.value }))} autoFocus
                />
                {/* Source toggle */}
                <div className="flex gap-2">
                  {([['system', 'System'], ['upload', 'My uploads']] as const).map(([src, label]) => (
                    <label key={src} className="flex items-center gap-1 cursor-pointer text-xs text-gray-600">
                      <input type="radio" name={`src-${design.id}`} value={src} checked={nameForm.source === src}
                        onChange={() => setNameForm(f => ({ ...f, source: src }))} className="accent-indigo-600" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Orientation */}
                <div className="flex items-center gap-1.5">
                  {(['Landscape', 'Portrait'] as const).map(o => (
                    <label key={o} className="flex items-center gap-1 cursor-pointer text-xs text-gray-600">
                      <input type="radio" checked={nameForm.isPortrait === (o === 'Portrait')}
                        onChange={() => {
                          const portrait = o === 'Portrait'
                          const key = portrait ? 'portraitSystemTemplateId' : 'landscapeSystemTemplateId'
                          const firstMatch = systemTemplates.find(t => t.isPortrait === portrait)
                          setNameForm(f => ({ ...f, isPortrait: portrait, [key]: f[key] || firstMatch?.id || '' }))
                        }}
                        className="accent-indigo-600 cursor-pointer" />
                      {o}
                    </label>
                  ))}
                </div>
                {nameForm.source === 'upload' ? (
                  <select className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={nameForm.templateId} onChange={e => setNameForm(f => ({ ...f, templateId: e.target.value }))}>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                ) : (
                  <>
                    <select className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      value={nameForm.isPortrait ? nameForm.portraitSystemTemplateId : nameForm.landscapeSystemTemplateId}
                      onChange={e => { const key = nameForm.isPortrait ? 'portraitSystemTemplateId' : 'landscapeSystemTemplateId'; setNameForm(f => ({ ...f, [key]: e.target.value })) }}>
                      {systemTemplates.filter(t => t.isPortrait === nameForm.isPortrait).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <div className="flex items-center gap-1.5">
                      <input type="color" value={nameForm.colour}
                        onChange={e => setNameForm(f => ({ ...f, colour: e.target.value }))}
                        className="h-6 w-6 cursor-pointer rounded border border-gray-300 p-0" title="Foreground colour" />
                      <input type="text" value={nameForm.colour}
                        onChange={e => setNameForm(f => ({ ...f, colour: e.target.value }))}
                        className="w-20 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input type="color" value={nameForm.backgroundColour}
                        onChange={e => setNameForm(f => ({ ...f, backgroundColour: e.target.value }))}
                        className="h-6 w-6 cursor-pointer rounded border border-gray-300 p-0" title="Background colour" />
                      <input type="text" value={nameForm.backgroundColour}
                        onChange={e => setNameForm(f => ({ ...f, backgroundColour: e.target.value }))}
                        className="w-20 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
                    </div>
                  </>
                )}
                <Button size="sm" className="h-6 text-xs gap-1" onClick={() => saveNameMutation.mutate()} disabled={saveNameMutation.isPending}>
                  <Check size={11} /> Save
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={cancelEdit}>Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-gray-900 truncate">{design.name}</p>
              <p className="text-xs text-gray-400">
                {isSystem && <span className="text-violet-500 mr-1">System</span>}
                {displayName}
                {design.primaryColour && (
                  <span className="inline-block ml-1.5 w-2.5 h-2.5 rounded-full border border-gray-200 align-middle" style={{ backgroundColor: design.primaryColour }} />
                )}
                {design.courseCount > 0 ? ` · ${design.courseCount} course${design.courseCount !== 1 ? 's' : ''}` : ''}
                {' · '}
                <span className={design.isPortrait ? 'text-indigo-500' : 'text-gray-400'}>
                  {design.isPortrait ? 'Portrait' : 'Landscape'}
                </span>
              </p>
            </>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {!editingName && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700"
              onClick={() => setEditingName(true)}>
              <Pencil size={13} />
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={onDelete}>
            <Trash2 size={13} />
          </Button>
          <button type="button" onClick={onToggle}
            className="ml-1 flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors">
            {expanded ? 'Close designer' : 'Open designer'}
          </button>
        </div>
      </div>

      {/* Inline designer */}
      {expanded && (
        <div className="border-t border-gray-100 p-4">
          {/* Settings strip — always visible when designer is open */}
          <div className="flex items-center gap-4 mb-4 pb-3 border-b border-gray-100 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-500">Orientation</span>
              <div className="flex border border-gray-300 rounded-md overflow-hidden">
                {(['Landscape', 'Portrait'] as const).map(o => {
                  const active = nameForm.isPortrait === (o === 'Portrait')
                  return (
                    <button key={o} type="button"
                      className={`px-2.5 py-1 cursor-pointer transition-colors ${active ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                      onClick={() => {
                        const portrait = o === 'Portrait'
                        const key = portrait ? 'portraitSystemTemplateId' : 'landscapeSystemTemplateId'
                        const firstMatch = systemTemplates.find(t => t.isPortrait === portrait)
                        const newForm = { ...nameForm, isPortrait: portrait, [key]: nameForm[key] || firstMatch?.id || '' }
                        setNameForm(newForm)
                        saveSettingsMutation.mutate(newForm)
                      }}>
                      {o}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="font-medium text-gray-500 shrink-0">Background</span>
              <div className="flex gap-1.5 overflow-x-auto flex-1 min-w-0 pb-0.5">
                {[
                  ...systemTemplates.filter(t => t.isPortrait === nameForm.isPortrait).map(t => ({ kind: 'system' as const, id: t.id, name: t.name, url: t.thumbnailUrl ?? null })),
                  ...templates.map(t => ({ kind: 'upload' as const, id: t.id, name: t.name, url: t.backgroundImageUrl ?? null })),
                ].map(bg => {
                  const activeSystemTemplateId = nameForm.isPortrait ? nameForm.portraitSystemTemplateId : nameForm.landscapeSystemTemplateId
                  const isSelected = bg.kind === 'system'
                    ? nameForm.source === 'system' && activeSystemTemplateId === bg.id
                    : nameForm.source === 'upload' && nameForm.templateId === bg.id
                  return (
                    <button key={`${bg.kind}-${bg.id}`} type="button" title={bg.name}
                      onClick={() => {
                        const sysKey = nameForm.isPortrait ? 'portraitSystemTemplateId' : 'landscapeSystemTemplateId'
                        const newForm = bg.kind === 'system'
                          ? { ...nameForm, source: 'system' as const, [sysKey]: bg.id }
                          : { ...nameForm, source: 'upload' as const, templateId: bg.id }
                        setNameForm(newForm)
                        saveSettingsMutation.mutate(newForm)
                      }}
                      className={`relative shrink-0 w-24 h-16 rounded overflow-hidden border-2 cursor-pointer transition-all ${isSelected ? 'border-indigo-500 shadow-sm' : 'border-transparent hover:border-gray-300'}`}
                      style={{
                        background: bg.kind === 'system' ? (nameForm.backgroundColour ?? '#ffffff') : undefined,
                        isolation: bg.kind === 'system' ? 'isolate' : undefined,
                      }}
                    >
                      {bg.url
                        ? (
                          <>
                            <img src={bg.url} alt={bg.name} className="w-full h-full object-cover" style={{ mixBlendMode: bg.kind === 'system' ? 'multiply' : undefined }} />
                            {bg.kind === 'system' && nameForm.colour && (
                              <div className="absolute inset-0" style={{ backgroundColor: nameForm.colour, mixBlendMode: 'screen' }} />
                            )}
                          </>
                        )
                        : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><ImagePlus size={11} className="text-gray-400" /></div>
                      }
                    </button>
                  )
                })}
                {systemTemplates.length === 0 && templates.length === 0 && (
                  <span className="text-gray-400 italic">No backgrounds yet — add some in the Backgrounds tab</span>
                )}
              </div>
            </div>
            {nameForm.source === 'system' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Colour</span>
                  <input type="color" value={nameForm.colour}
                    onChange={e => setNameForm(f => ({ ...f, colour: e.target.value }))}
                    onBlur={() => saveSettingsMutation.mutate(nameForm)}
                    className="h-6 w-6 cursor-pointer rounded border border-gray-300 p-0" />
                  <input type="text" value={nameForm.colour}
                    onChange={e => setNameForm(f => ({ ...f, colour: e.target.value }))}
                    onBlur={() => saveSettingsMutation.mutate(nameForm)}
                    className="w-20 border border-gray-300 rounded px-2 py-1 font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-500">Background</span>
                  <input type="color" value={nameForm.backgroundColour}
                    onChange={e => setNameForm(f => ({ ...f, backgroundColour: e.target.value }))}
                    onBlur={() => saveSettingsMutation.mutate(nameForm)}
                    className="h-6 w-6 cursor-pointer rounded border border-gray-300 p-0" />
                  <input type="text" value={nameForm.backgroundColour}
                    onChange={e => setNameForm(f => ({ ...f, backgroundColour: e.target.value }))}
                    onBlur={() => saveSettingsMutation.mutate(nameForm)}
                    className="w-20 border border-gray-300 rounded px-2 py-1 font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500" />
                </div>
              </>
            )}
          </div>
          <div className="relative">
            <CertificateFieldDesigner
              templateId={nameForm.source === 'upload' ? nameForm.templateId : null}
              systemTemplateId={nameForm.source === 'system' ? (nameForm.isPortrait ? nameForm.portraitSystemTemplateId : nameForm.landscapeSystemTemplateId) : null}
              primaryColour={nameForm.source === 'system' ? nameForm.colour : null}
              backgroundColour={nameForm.source === 'system' ? nameForm.backgroundColour : null}
              fieldsJson={design.fieldsJson}
              isPortrait={nameForm.isPortrait}
              onChange={json => updateMutation.mutate(json)}
            />
            {/* Auto-save indicator */}
            <div className={`absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-300 pointer-events-none ${
              updateMutation.isPending
                ? 'bg-gray-100 text-gray-500 opacity-100'
                : autoSaved
                  ? 'bg-green-100 text-green-700 opacity-100'
                  : 'opacity-0'
            }`}>
              {updateMutation.isPending
                ? (<><Loader2 size={11} className="animate-spin" /> Saving…</>)
                : (<><Check size={11} /> Saved</>)
              }
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Third-party tab ────────────────────────────────────────────────────────

function ThirdPartyTab() {
  const queryClient = useQueryClient()
  const [courseId, setCourseId] = useState('')

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })
  const thirdPartyCourses = courses.filter(c => c.isThirdPartyCertified)

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['pending-third-party-certificates', courseId],
    queryFn: () => adminApi.getPendingThirdPartyCertificates(courseId).then(r => r.data),
    enabled: !!courseId,
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-sm font-medium text-gray-900">Third-party certificates</h2>
        <p className="text-xs text-gray-500 mt-1 max-w-2xl">
          For courses certified by an external awarding body. Pick the course, then upload the PDFs
          they've sent you — CertSlate will suggest who each one belongs to based on the filename,
          but you confirm before anything is saved.
        </p>
      </div>

      <div className="max-w-xs">
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Course</label>
        <select
          value={courseId}
          onChange={e => setCourseId(e.target.value)}
          className="w-full h-9 text-sm border border-gray-200 rounded-lg px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">Select a course…</option>
          {thirdPartyCourses.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        {thirdPartyCourses.length === 0 && (
          <p className="text-xs text-amber-600 mt-1.5">
            No third-party certified courses yet — enable it on a course in the Courses section.
          </p>
        )}
      </div>

      {courseId && (
        <ThirdPartyCertificateUploader
          key={courseId}
          pending={pending.map(p => ({ id: p.trainingRecordId, name: p.staffMemberName, meta: p.clientName, completedDate: p.completedDate }))}
          isLoading={isLoading}
          providerName={courses.find(c => c.id === courseId)?.thirdPartyProviderName}
          emptyMessage="Nobody is waiting on a certificate for this course."
          onUploaded={() => queryClient.invalidateQueries({ queryKey: ['pending-third-party-certificates', courseId] })}
          upload={(id, file) => adminApi.uploadCertificate(id, file)}
        />
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function CertificateTemplatesPage() {
  useTopBar(<span className="text-sm font-medium text-gray-900">Certificates</span>)
  const [tab, setTab] = useState<Tab>('designs')

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {(['designs', 'backgrounds', 'images', 'thirdParty'] as Tab[]).map(t => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px cursor-pointer transition-colors ${
              tab === t
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'designs' && <DesignsTab />}
      {tab === 'backgrounds' && <BackgroundsTab />}
      {tab === 'images' && <ImagesTab />}
      {tab === 'thirdParty' && <ThirdPartyTab />}
    </div>
  )
}
